<template>
  <div id="closet-home">
    <div class="page-header">
      <div class="title">나의 옷장</div>
      <div class="subtitle">내 몸에 딱 맞는 사이즈를 기록하고 관리하세요</div>
    </div>

    <!-- ===== 커뮤니티 통계 (비로그인 포함 전체 공개) ===== -->
    <section class="community-section">
      <h2 class="section-title">브리저 옷장 현황</h2>

      <!-- KPI -->
      <div class="cs-kpis">
        <template v-if="statsLoading">
          <div class="cs-kpi" v-for="n in 3" :key="'sk'+n">
            <div class="cs-sk cs-sk-val"></div>
            <div class="cs-sk cs-sk-label"></div>
          </div>
        </template>
        <template v-else>
          <div class="cs-kpi">
            <div class="cs-value">{{ pub.totalClothing.toLocaleString() }}<span>벌</span></div>
            <div class="cs-label">등록된 의류</div>
          </div>
          <div class="cs-kpi">
            <div class="cs-value">{{ pub.totalUsers.toLocaleString() }}<span>명</span></div>
            <div class="cs-label">기록 중인 사용자</div>
          </div>
          <div class="cs-kpi">
            <div class="cs-value">{{ pub.totalBrands.toLocaleString() }}<span>개</span></div>
            <div class="cs-label">기록된 브랜드</div>
          </div>
        </template>
      </div>

      <!-- 분포 -->
      <div class="cs-grid" v-if="!statsLoading">
        <div class="cs-card">
          <div class="cs-card-title">카테고리별 등록</div>
          <div class="dist-list">
            <div class="dist-row" v-for="c in categoryDist" :key="c.label">
              <span class="dist-label">{{ c.label }}</span>
              <div class="dist-bar-wrap"><div class="dist-bar" :style="{ width: c.ratio + '%' }"></div></div>
              <span class="dist-val">{{ c.count }}벌</span>
            </div>
            <div class="dist-empty" v-if="categoryDist.length === 0">데이터 없음</div>
          </div>
        </div>

        <div class="cs-card">
          <div class="cs-card-title">사용자 분포 <span class="cs-sub">성별·연령대</span></div>
          <div class="pill-rows">
            <div class="pill-row" v-if="genderDist.length">
              <span class="pill-cap">성별</span>
              <span class="stat-pill" v-for="g in genderDist" :key="g.label">{{ g.label }} <b>{{ g.count }}</b></span>
            </div>
            <div class="pill-row" v-if="ageDist.length">
              <span class="pill-cap">연령대</span>
              <span class="stat-pill" v-for="a in ageDist" :key="a.label">{{ a.label }} <b>{{ a.count }}</b></span>
            </div>
            <div class="dist-empty" v-if="!genderDist.length && !ageDist.length">데이터 없음</div>
          </div>

          <div class="cs-card-title tb">인기 브랜드</div>
          <div class="brand-chips">
            <span class="brand-chip" v-for="(b, i) in pub.topBrands" :key="b.brandName">
              <em class="rank">{{ i + 1 }}</em>{{ b.brandName }} <b>{{ b.count }}벌</b>
            </span>
            <div class="dist-empty" v-if="!pub.topBrands || pub.topBrands.length === 0">데이터 없음</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== 내 요약 (로그인) ===== -->
    <section class="my-section" v-if="isLoggedIn">
      <h2 class="section-title">내 옷장 요약</h2>
      <div class="my-grid">
        <!-- 내 요약 카드 -->
        <div class="my-card">
          <div class="my-kpis">
            <div class="my-kpi">
              <div class="mk-value">{{ allClothingList.length }}<span>벌</span></div>
              <div class="mk-label">내 의류</div>
            </div>
            <div class="my-kpi">
              <div class="mk-value">{{ myBrandCount }}<span>개</span></div>
              <div class="mk-label">브랜드</div>
            </div>
            <div class="my-kpi">
              <div class="mk-value">{{ perfectCount }}<span>벌</span></div>
              <div class="mk-label">딱 맞음</div>
            </div>
            <div class="my-kpi" v-if="hasBodyData">
              <div class="mk-value">{{ bodyData.height || '-' }}<span>cm</span></div>
              <div class="mk-label">키 · {{ bodyData.weight || '-' }}kg</div>
            </div>
          </div>

          <div class="quick-actions">
            <button class="qa-btn" @click="goToClothingRegister">
              <span class="qa-ic">👕</span><span class="qa-t">의류 등록</span>
            </button>
            <button class="qa-btn" @click="goToWardrobe">
              <span class="qa-ic">🗄️</span><span class="qa-t">옷장 열기</span>
            </button>
            <button class="qa-btn" @click="goToGuide">
              <span class="qa-ic">🔎</span><span class="qa-t">사이즈 가이드</span>
            </button>
            <button class="qa-btn" @click="goToBody">
              <span class="qa-ic">📏</span><span class="qa-t">신체정보</span>
            </button>
          </div>
        </div>

        <!-- 최근 등록 -->
        <div class="my-card recent">
          <div class="my-card-head">
            <span>최근 등록 의류</span>
            <button v-if="recentClothingList.length" class="more-link" @click="goToWardrobe">더보기</button>
          </div>
          <div v-if="recentClothingList.length === 0" class="my-empty">
            <p>아직 등록한 의류가 없어요</p>
            <button class="btn-register" @click="goToClothingRegister">첫 의류 등록하기</button>
          </div>
          <div v-else class="recent-list">
            <div class="recent-item" v-for="item in recentClothingList" :key="item.id" @click="goToClothingDetail(item.id)">
              <span class="ri-cat">{{ getCategoryLabel(item.category) }}</span>
              <span class="ri-brand">{{ item.brandName || '-' }}</span>
              <span class="ri-name">{{ item.productName || item.subCategory || '' }}</span>
              <span class="ri-size" v-if="item.labeledSize">{{ item.labeledSize }}</span>
              <span class="ri-fit" :class="fitClass(item.fitFeedback)" v-if="item.fitFeedback">{{ getFitFeedbackLabel(item.fitFeedback) }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== 비로그인 CTA ===== -->
    <section class="cta-section" v-else>
      <div class="cta-card">
        <div class="cta-text">
          <div class="cta-title">내 옷장도 만들어 보세요</div>
          <div class="cta-desc">
            산 옷의 브랜드·사이즈·실측·핏을 기록하면, 재구매 때 바로 참고하고
            나에게 맞는 사이즈도 추천받을 수 있어요.
          </div>
        </div>
        <button class="cta-btn" @click="goToLogin">시작하기</button>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import * as closetApi from "../api";
import * as routerUtil from "@/common/utils/routerUtil";
import { getCategoryLabel, getFitFeedbackLabel, CLOTHING_CATEGORY_OPTIONS } from "../class";

const GENDER_LABEL: Record<string, string> = { MALE: "남성", FEMALE: "여성", UNKNOWN: "미상" };
const AGE_ORDER = ["10대", "20대", "30대", "40대", "50대 이상", "미상"];

export default defineComponent({
  name: "ClosetHomeView",

  setup() {
    const store = useStore();
    const router = useRouter();
    const bodyData = ref<any>(null);
    const allClothingList = ref<any[]>([]);
    const statsLoading = ref(true);
    const pub = ref<any>({
      totalClothing: 0,
      totalUsers: 0,
      totalBrands: 0,
      categoryCounts: {},
      genderCounts: {},
      ageGroupCounts: {},
      topBrands: [],
    });

    const isLoggedIn = computed(() => !!store.state.loginInfo?.id);
    const hasBodyData = computed(() => {
      if (!bodyData.value) return false;
      return bodyData.value.height || bodyData.value.weight;
    });

    const recentClothingList = computed(() => allClothingList.value.slice(0, 5));

    const myBrandCount = computed(() => {
      const s = new Set<string>();
      allClothingList.value.forEach((c: any) => {
        if (c.brandName) s.add(c.brandName.trim());
      });
      return s.size;
    });

    const perfectCount = computed(
      () => allClothingList.value.filter((c: any) => c.fitFeedback === "PERFECT").length
    );

    // ===== 커뮤니티 분포 가공 =====
    const categoryDist = computed(() => {
      const counts = pub.value.categoryCounts || {};
      const total = Object.values(counts).reduce((a: number, b: any) => a + Number(b), 0) || 1;
      return CLOTHING_CATEGORY_OPTIONS
        .filter((o) => counts[o.value])
        .map((o) => ({
          label: o.label,
          count: Number(counts[o.value]),
          ratio: (Number(counts[o.value]) / total) * 100,
        }))
        .sort((a, b) => b.count - a.count);
    });

    const genderDist = computed(() => {
      const counts = pub.value.genderCounts || {};
      return Object.entries(counts)
        .filter(([, v]) => Number(v) > 0)
        .map(([k, v]) => ({ label: GENDER_LABEL[k] || k, count: Number(v) }))
        .sort((a, b) => b.count - a.count);
    });

    const ageDist = computed(() => {
      const counts = pub.value.ageGroupCounts || {};
      return AGE_ORDER.filter((k) => counts[k])
        .map((k) => ({ label: k, count: Number(counts[k]) }));
    });

    const fitClass = (v: string): string => {
      return (
        {
          VERY_TIGHT: "fit-t",
          TIGHT: "fit-t",
          PERFECT: "fit-ok",
          LOOSE: "fit-l",
          VERY_LOOSE: "fit-l",
        } as Record<string, string>
      )[v] || "";
    };

    const fetchPublicStats = async () => {
      statsLoading.value = true;
      try {
        const data = await closetApi.getPublicStats();
        if (data) pub.value = data;
      } catch {
        // ignore
      } finally {
        statsLoading.value = false;
      }
    };

    const fetchBody = async () => {
      if (!isLoggedIn.value) return;
      try {
        const data = await closetApi.getMyBody();
        if (data) bodyData.value = data;
      } catch {
        // ignore
      }
    };

    const fetchClothingList = async () => {
      if (!isLoggedIn.value) return;
      try {
        const data = await closetApi.getClothingList();
        if (data && Array.isArray(data)) allClothingList.value = data;
      } catch {
        // ignore
      }
    };

    const goToBody = () => router.push("/closet/body");
    const goToWardrobe = () => router.push("/closet/wardrobe");
    const goToGuide = () => router.push("/closet/guide");
    const goToClothingRegister = () => router.push({ name: "closet-clothing-register" });
    const goToClothingDetail = (id: number) =>
      router.push({ name: "closet-clothing-detail", params: { id } });
    const goToLogin = () => {
      const nextRoute = encodeURIComponent(JSON.stringify({ path: "/closet/home" }));
      routerUtil.moveTo("/user/login", nextRoute);
    };

    onMounted(() => {
      fetchPublicStats();
      fetchBody();
      fetchClothingList();
    });

    return {
      statsLoading,
      pub,
      categoryDist,
      genderDist,
      ageDist,
      bodyData,
      isLoggedIn,
      hasBodyData,
      allClothingList,
      recentClothingList,
      myBrandCount,
      perfectCount,
      fitClass,
      goToBody,
      goToLogin,
      goToWardrobe,
      goToGuide,
      goToClothingRegister,
      goToClothingDetail,
      getCategoryLabel,
      getFitFeedbackLabel,
    };
  },
});
</script>

<style lang="scss">
@import "../assets/css/index.scss";
</style>
